






































































































import CommonBase from "../CommonBase";
import {Company, DateTime, IdCard, PhoneNumber, SiteInOutReportLine} from "../../modules/models";
import {Component, Inject, Prop, Watch} from "vue-property-decorator";
import DatePicker from "../DatePicker.vue";
import PersonMarker from "../PersonMarker.vue";
import Api from "./Api";
import {numOrNull} from "../../modules/utils";
import Routes from "../../modules/Routes";
import CardIcon from "../CardIcon.vue";
import {ErrorColor, UnimportantColor, WarningColor, DefaultColor} from "../../modules/constants";

const left = "left";

@Component({
    components: {
        "at-date-picker": DatePicker,
        "at-person-marker": PersonMarker,
        "at-cardicon": CardIcon,
    }
})
export default class LocationRegistrationReport extends CommonBase{
    @Prop()
    locationId!: number;
    @Prop()
    companyOrgnum!: number;

    loading = false;

    @Inject()
    api!: Api;

    dateMode = "single";
    from = DateTime.now().toMidnight();
    to = this.from.nextDay();

    search = "";

    unimportantColor = UnimportantColor+"8a";
    defaultCardColor = DefaultColor;

    @Watch("to")
    checkTo(newTo: DateTime) {
        // from date should always be at least previous day of todate
        if(newTo.millis() <= this.from.millis()) {
            this.from = newTo.prevDay();
        }
        if(this.dateMode != "single") {
            this.debounceLoadReportlines();
        }
    }
    // todate should alway be next day when we're choosing single days, and at least next day when choosing a date interval.
    @Watch("from")
    checkFrom(newFrom: DateTime) {
        if(this.dateMode == "single") {
            this.to = newFrom.nextDay();
        } else if(newFrom.millis() >= this.to.millis()) {
            this.to = newFrom.nextDay();
        }
        this.debounceLoadReportlines();
    }
    @Watch("filterOrgNum")
    filterOrgNumChanged() {
        this.debounceLoadReportlines();
    }

    resultHeaders = [
        {text: "Inn", value: "timeIn", align: left},
        {text: "Ut", value: "timeOut", align: left},
        {text: "Fornavn", value: "firstName", align: left},
        {text: "Etternavn", value: "lastName", align: left},
        {text: "Født", value: "birthDate", align: left},
        {text: "Firma", value: "companyName", align: left},
        {text: "Kortnr", value: "cardNum", align: left},
        {text: "Telefon", value: "phoneNumber", align: left, sortable: false}
    ];
    resultItems: SiteInOutReportLine[] = [];

    tablefilter<K extends keyof SiteInOutReportLine>(val: SiteInOutReportLine[K], search: string): boolean {
        let strval = "";
        if(val != null) {
            if(val instanceof DateTime) {
                strval = val.toLocalDateTime();
            }
            if(val instanceof PhoneNumber) {
                strval = val.toString();
            }
            if(typeof val == "number") {
                strval = (val as number).toString();
            }
            if(typeof val == "string") {
                strval = (val as string).toLocaleLowerCase();
            }
        }
        return strval.includes(search);
    }

    private debounceMillis: number = 400;
    private debounceId: number = 0;
    debounceLoadReportlines() {
        if(this.debounceId == 0) {
            // if no debounce is active set delay for next debounce, load report lines immediatly
            const dbId = setTimeout(()=>{
                this.debounceId = 0;
            }, this.debounceMillis);
            this.debounceId = typeof dbId == "number" ? dbId : 0;
            this.loadReportlines();
        } else {
            // cancel existing call, set new
            clearTimeout(this.debounceId);
            const dbId = setTimeout(() => {
                this.loadReportlines();
                this.debounceId = 0;
            }, this.debounceMillis);
            this.debounceId = typeof dbId == "number" ? dbId : 0;
        }
    }

    async loadReportlines() {
        this.loading = true;
        try {
          this.resultItems = await this.api.loadReportData(this.companyOrgnum, this.locationId, this.from, this.to, this.filterOrgNum);
        } finally {
          this.loading = false;
        }
    }

    formatDateTime(dt: DateTime): string | null {
        return dt != null ? dt.toLocalDateTime(): null;
    }
    formatDate(dt: DateTime): string | null {
        return dt != null ? dt.toLocalDate() : null;
    }
    numberOrNull(n: number): number | null {
        return numOrNull(n);
    }
    prevDay() {
        this.from = this.from.prevDay();
    }
    nextDay() {
        this.from = this.from.nextDay();
    }

    // List of companies in result. So that we can offer to filter on them
    get distinctCompanies(): Company[] {
        let companies = this.resultItems.map((r: SiteInOutReportLine) => r.company()).filter((c: Company, i: number, self: Company[]) => self.indexOf(c) == i);
        if(this.filterCompany != null) { // add filterCompany so its displayed in the select, and clearable there.
            companies.push(this.filterCompany);
        }
        return companies;
    }
    // if user choose to filter on a company, this is set
    filterCompany: Company | null = null;
    set filterOrgNum(num: number) {
        if(num > 0) {
            this.filterCompany = this.distinctCompanies.find((c: Company) => c.orgNum === num) || null;
        } else {
            this.filterCompany = null;
        }
    }
    get filterOrgNum() {
        return this.filterCompany != null ? this.filterCompany.orgNum : 0;
    }
    get excelReportUrl() {
        return Routes.siteReportXlsx(this.companyOrgnum, this.locationId, this.from, this.to, this.filterOrgNum);
    }
    cardColor(srl: SiteInOutReportLine): string {
        let clr = srl.phoneNumber == null ? this.defaultCardColor : this.unimportantColor;
        if(IdCard.isExpired(srl.expirationDate, srl.timeIn)) {
            clr = ErrorColor;
        } else if(IdCard.isSoonExpired(srl.expirationDate, srl.timeIn)) {
            clr = WarningColor;
        }
        return clr;
    }
    cardTooltipTxt(srl: SiteInOutReportLine): string {
        let txt = srl.phoneNumber != null ? 'Kort tilknyttet innlogget bruker' : 'Innregistrert med kort på kortleser';
        if(IdCard.isExpired(srl.expirationDate, srl.timeIn)) {
            txt = `Kort er utgått på dato (${srl.expirationDate.toLocalDate()})`;
        } else if(IdCard.isSoonExpired(srl.expirationDate, srl.timeIn)) {
            txt = `Kortets utløpsdato nærmer seg (${srl.expirationDate.toLocalDate()})`;
        }
        return txt;
    }

    filteroutPhone(pns: PhoneNumber[], pn: PhoneNumber | null) {
      if(pn == null) {
        return pns;
      } else {
        return pns.filter(r => !r.equals(pn));
      }
    }

    // hooks
    created() {
        if(this.locationId > 0) {
            this.loadReportlines();
        }
    }
}
